import {Component, OnDestroy, OnInit, Inject} from "@angular/core";
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import {debounceTime} from "rxjs/operators";
import moment = require("moment");
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { mkConfig, generateCsv, download } from "export-to-csv";
import {DatePipe} from "@angular/common";
import {NgbPopoverConfig} from "@ng-bootstrap/ng-bootstrap";
import {AuthenticationService} from "../../services/authentication.service";
import {CloudApiService} from "../../services/cloud-api.service";
import {UserSessionService} from "../../services/user-session.service";
import {SelectLibraryModal} from "../modals/select-library/select-library.modal.component";
import {BranchDto} from "../../services/dtos/branch.dto";
import {SearchDto} from "../../services/dtos/search.dto";
import {ReportConfig} from "../../reports/dtos/interfaces/report-config";
import {ReportDescriptor} from "../../reports/dtos/interfaces/report-descriptor";
import {ReportGroup} from "../../reports/dtos/interfaces/report-group";
import {REPORT_REGISTRY, CATEGORY_ICONS} from "../../reports/report-registry";
import {Period} from "../../services/dtos/enums/period.enum";

@Component({
    selector: 'app-reports',
    template: require('./reports.component.html'),
    styles: [ require('./reports.component.css') ],
    providers: [NgbPopoverConfig, DatePipe]
})

export class ReportsComponent implements OnInit, OnDestroy {
    errorMessage: string;

    branches: BranchDto[] = [];
    periods = Object.keys(Period).map(k => Period[k]);

    selectedBranch: BranchDto = new BranchDto();
    selectedPeriod: Period = Period.Daily;

    unixEpoch: string;
    fromDateModel: string;
    toDateModel: string;
    isFromDateInvalid: boolean;
    isToDateInvalid: boolean;

    searchQuery: SearchDto | null = null;

    groups: ReportGroup[] = [];
    selectedReportId: string;
    activeDescriptor: ReportDescriptor | null = null;

    private librarySubscription: Subscription;
    private dateChanged = new Subject<void>();
    private dateChangedSub: Subscription;
    private reportParamSub: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userSession: UserSessionService,
        private cloudApiService: CloudApiService,
        private authenticationService: AuthenticationService,
        private selectLibraryModal: SelectLibraryModal,
        private datePipe: DatePipe,
        @Inject(REPORT_REGISTRY) private registry: ReportConfig[],
    ) {}

    ngOnInit(): void {
        this.groups = this.groupByCategory(this.registry);
        this.reportParamSub = this.route.queryParamMap.subscribe(params => {
            const requestedReportId = params.get('report');
            const isRequestedReportValid = requestedReportId && this.registry.some(report => report.id === requestedReportId);
            const id = isRequestedReportValid ? requestedReportId : (this.registry.length ? this.registry[0].id : null); // default to first report if invalid report param

            if (!isRequestedReportValid && id) {
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: {report: id},
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                });
                return;
            }

            if (id === this.selectedReportId) return;
            this.selectedReportId = id;
            this.activeDescriptor = null;
            this.errorMessage = null;
            this.searchQuery = this.createSearchQuery();
        });

        const today = new Date();
        this.unixEpoch     = this.datePipe.transform(0, 'yyyy-MM-dd');
        this.fromDateModel = this.datePipe.transform(today, 'yyyy-MM-dd');
        this.toDateModel   = this.datePipe.transform(today, 'yyyy-MM-dd');
        this.searchQuery   = this.createSearchQuery();

        this.dateChangedSub = this.dateChanged
            .pipe(debounceTime(300))
            .subscribe(() => { this.searchQuery = this.createSearchQuery(); });

        this.librarySubscription = this.userSession.library.subscribe(library => {
            if (library) this.loadBranches();
            else if (!this.userSession.libraryId && this.authenticationService.authenticated)
                this.selectLibraryModal.open();
        });
    }

    ngOnDestroy(): void {
        this.librarySubscription.unsubscribe();
        if (this.dateChangedSub) this.dateChangedSub.unsubscribe();
    }

    get selectedConfig(): ReportConfig | null {
        return this.registry.find(report => report.id === this.selectedReportId) || null;
    }

    get canExport(): boolean {
        return !!this.activeDescriptor && this.activeDescriptor.rows.length > 0;
    }

    selectReport(id: string): void {
        if (id === this.selectedReportId) return;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { report: id },
            queryParamsHandling: 'merge',
        });
    }

    changeBranch(branch: BranchDto): void {
        this.selectedBranch = branch;
        this.userSession.setBranchId(branch.id);
        this.searchQuery = this.createSearchQuery();
    }

    changePeriod(period: Period): void {
        this.selectedPeriod = period;
        this.searchQuery = this.createSearchQuery();
    }

    refresh(): void {
        this.searchQuery = this.createSearchQuery();
    }

    onDateChanged(): void {
        this.dateChanged.next();
    }

    onDescriptor(descriptor: ReportDescriptor): void {
        this.activeDescriptor = descriptor;
    }

    onReportFailed(message: string): void {
        this.errorMessage = message;
    }

    public exportTo(type: string): void {
        let fromDate = moment.utc(this.fromDateModel).local().format('YYYY-MM-DD');
        let toDate = moment.utc(this.toDateModel).local().format('YYYY-MM-DD');
        const name = `${this.activeDescriptor.title.replace(/\s+/g, '-')}-${fromDate}-${toDate}`;

        if (type === 'pdf') this.exportToPdf(name, fromDate, toDate);
        if (type === 'csv') this.exportToCsv(name);
    }

    private exportToPdf(name: string, fromDate: string, toDate: string): void {
        const { title, columns, rows, totalRows } = this.activeDescriptor;
        const doc: any = new jsPDF('landscape');

        const totalPagesExp = '{total_pages_count_string}';
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();

        doc.setFontSize(16);
        doc.text(`${title} Report`, pageWidth / 2, 10, 'center');

        doc.setFontSize(12);
        doc.text(`from ${fromDate} to ${toDate}`, pageWidth / 2, 18, 'center');
        doc.text(`Branch: ${this.selectedBranch.name}`, pageWidth / 2, 26, 'center');

        doc.autoTable({
            head: [columns.map(c => c.label)],
            body: rows.map(r => columns.map(c => r[c.key])),
            startY: 32,
            didDrawPage: (data: any) => {
                const margin = data.settings.margin.left;
                const y = pageHeight - 10;

                // row count
                doc.setFontSize(10);
                doc.setFont(undefined, 'italic');
                doc.text(`Report contains ${rows.length} of ${totalRows} row${rows.length == 1 ? '' : 's'}.`, margin, y);

                // page count
                let str = 'Page ' + doc.internal.getNumberOfPages();
                if (typeof doc.putTotalPages === 'function') {
                    str = str + ' of ' + totalPagesExp;
                }
                doc.setFont(undefined, 'normal');
                doc.text(str, pageWidth + margin * 1.5, y, { align: 'right' });
            }
        });

        if (typeof doc.putTotalPages === 'function') {
            doc.putTotalPages(totalPagesExp);
        }

        doc.save(`${name}.pdf`);
    }

    private exportToCsv(name: string): void {
        const { columns, rows, totalRows } = this.activeDescriptor;
        const csvConfig = mkConfig({
            fieldSeparator: ',',
            filename: name,
            columnHeaders: columns.map(c => ({ key: c.key, displayLabel: c.label })),
        });

        const note = `# Report contains ${rows.length} of ${totalRows} row${rows.length == 1 ? '' : 's'}.`;
        const csvBody = (generateCsv(csvConfig)(rows) as any as string).replace(/^\uFEFF/, '');
        const csvWithNote = `\uFEFF${note}\n${csvBody}`;
        download(csvConfig)(csvWithNote as any);
    }

    private loadBranches(): void {
        this.cloudApiService.getBranches().subscribe(
            branches => {
                let allBranch = new BranchDto();
                allBranch.id = 0;
                allBranch.name = "All Branches";

                this.branches = branches;
                this.branches.unshift(allBranch);

                let previousBranch = branches.find(x => x.id === this.userSession.branchId);
                this.changeBranch(previousBranch !== undefined ? previousBranch : allBranch);
            },
            error => {
                this.errorMessage = error;
            }
        );
    }

    private isDateInvalid(date: Date) {
        return isNaN(date.getTime()) || date.getFullYear() < 1970 || date.getFullYear() > 9999;
    }

    private createSearchQuery(): SearchDto {
        let searchQuery = new SearchDto();

        if (this.selectedConfig && this.selectedConfig.usesPeriod && this.selectedPeriod) {
            searchQuery.groupBy = this.selectedPeriod;
            searchQuery.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }

        searchQuery.fromDateUtc = moment(this.fromDateModel).utc().toDate();
        searchQuery.toDateUtc = moment(this.toDateModel).add({'hours': 24}).utc().toDate();

        this.isFromDateInvalid = this.isDateInvalid(searchQuery.fromDateUtc);
        this.isToDateInvalid = this.isDateInvalid(searchQuery.toDateUtc);

        if (this.isFromDateInvalid || this.isToDateInvalid)
            return null;

        return searchQuery;
    }

    private groupByCategory(reportConfigs: ReportConfig[]): ReportGroup[] {
        const groups: ReportGroup[] = [];
        for (const reportConfig of reportConfigs) {
            let group = groups.find(x => x.category === reportConfig.category);
            if (!group) {
                group = {
                    category: reportConfig.category,
                    icon: CATEGORY_ICONS[reportConfig.category],
                    items: []
                };
                groups.push(group);
            }
            group.items.push(reportConfig);
        }
        return groups;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/components/reports/reports.component.ts